import { ReactNode } from 'react';
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DialogShellProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  // Convenience footer props — use when footer is just Cancel + Submit
  onCancel?: () => void;
  onSubmit?: () => void;
  submitText?: string;
  submitDisabled?: boolean;
  isSubmitting?: boolean;
  isDirty?: boolean;
}

export const DialogShell = ({
  icon,
  title,
  description,
  children,
  footer,
  onCancel,
  submitText = 'Save',
  submitDisabled,
  isSubmitting,
  isDirty,
}: DialogShellProps) => (
  <>
    {/* ── Header ── */}
    <DialogHeader className="px-6 py-4 border-b border-border bg-muted/30 space-y-0">
      <div className="flex items-center gap-3">
        {icon && (
          <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
            {icon}
          </div>
        )}
        <div>
          <DialogTitle className="text-sm font-semibold leading-tight">
            {title}
          </DialogTitle>
          {description && (
            <DialogDescription className="text-xs text-muted-foreground mt-0.5">
              {description}
            </DialogDescription>
          )}
        </div>
      </div>
    </DialogHeader>

    {/* ── Body ── */}
    <div className="px-6 py-5 space-y-5 overflow-y-auto flex-1">{children}</div>

    {/* ── Footer ── */}
    {(footer || onCancel) && (
      <DialogFooter className="px-6 py-4 border-t border-border bg-muted/20 flex items-center justify-end gap-2">
        {footer ?? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onCancel}
              className="h-8 text-xs"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              className="h-8 text-xs gap-1.5"
              disabled={submitDisabled ?? isDirty === false}
            >
              {isSubmitting && <Loader2 className="h-3 w-3 animate-spin" />}
              {submitText}
            </Button>
          </>
        )}
      </DialogFooter>
    )}
  </>
);
